// src/components/checkout/ShippingForm.jsx
import React, { useState, useEffect } from 'react';
import Button from '../common/Button';

function ShippingForm({ userDetails, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    phone: '', // added phone field – Paystack likes having it
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (userDetails) {
      setFormData({
        name: userDetails.name || userDetails.full_name || '',
        email: userDetails.email || '',
        street: userDetails.address?.street || '',
        city: userDetails.address?.city || '',
        state: userDetails.address?.state || '',
        zip: userDetails.address?.zip || '',
        country: userDetails.address?.country || 'Nigeria',
        phone: userDetails.phone_number || userDetails.phone || '',
      });
    }
  }, [userDetails]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.street.trim()) newErrors.street = 'Street address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zip.trim()) newErrors.zip = 'ZIP/Postal code is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    // Phone is optional but nice to have
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is recommended';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      console.log('[ShippingForm] Validation passed → submitting:', formData);
      onSubmit(formData, true);           // <-- explicitly pass true
      setIsSubmitted(true);
    } else {
      console.log('[ShippingForm] Validation failed:', errors);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-playfair-display text-primary mb-4">Delivery Details</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-montserrat text-secondary mb-1">Full Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-montserrat text-secondary mb-1">Email Address *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-montserrat text-secondary mb-1">Phone Number *</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="+234 801 234 5678"
          />
        </div>

        <div>
          <label className="block text-sm font-montserrat text-secondary mb-1">Street Address *</label>
          <input
            type="text"
            name="street"
            value={formData.street}
            onChange={handleChange}
            className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
              errors.street ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.street && <p className="text-red-500 text-sm mt-1">{errors.street}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-montserrat text-secondary mb-1">City *</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
          </div>

          <div>
            <label className="block text-sm font-montserrat text-secondary mb-1">State *</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                errors.state ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-montserrat text-secondary mb-1">ZIP/Postal Code *</label>
            <input
              type="text"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                errors.zip ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.zip && <p className="text-red-500 text-sm mt-1">{errors.zip}</p>}
          </div>

          <div>
            <label className="block text-sm font-montserrat text-secondary mb-1">Country *</label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                errors.country ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              placeholder="Nigeria"
            />
            {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
          </div>
        </div>

        <Button
          type="submit"
          className="w-full bg-primary text-cream px-6 py-3.5 text-lg font-montserrat rounded-full hover:bg-primary/90 mt-6"
          disabled={isSubmitted}
        >
          {isSubmitted ? 'Delivery Details Saved ✓' : 'Save & Continue to Payment'}
        </Button>
      </form>
    </div>
  );
}

export default ShippingForm;